/**
 * Builds the query string for a catalog - movies, TV shows or
 * autographs, which all send their filters the same way.
 *
 * Replaces a long chain of template literals, for two reasons beyond
 * length. Nothing was encoded, so a collection called "Fast & Furious"
 * cut the URL short at the ampersand and silently dropped every filter
 * after it. And a filter can now hold several values, which has to
 * arrive as a repeated parameter (?genre=Action&genre=Comedy) rather
 * than a joined string - the backend matches each value against a name,
 * so "Action,Comedy" would be looked up as a genre by that literal name.
 */

/** The API's name for each argument, where the two differ. */
const PARAM_NAMES: Record<string, string> = {
  selectedArtistId: 'selected_artist_id',
  signedType: 'signed_type',
  pictureType: 'picture_type',
};

/**
 * Arguments the cache key leaves out.
 *
 * Only `page`, and only because every page of one query is meant to
 * land in a single entry - see catalogQueryCacheKey. Anything else that
 * changes the response has to change the key, which is why `mode` is
 * not here despite not being a filter either.
 */
const NOT_IN_THE_CACHE_KEY = ['page'];

const FILTER_KEYS = [
  'keyword',
  'tag',
  'collection',
  'studio',
  'network',
  'genre',
  'language',
  'subtitle',
  'country',
  'year',
  'rating',
  'quality',
  'size',
  'signedType',
  'pictureType',
  'selectedArtistId',
];

const append = (parts: string[], name: string, value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return;
  }
  parts.push(`${name}=${encodeURIComponent(String(value))}`);
};

export const buildCatalogQuery = (args: any): string => {
  const parts: string[] = [];
  append(parts, 'page', args?.page || 1);
  if (args?.mode) {
    append(parts, 'mode', args.mode);
  }

  for (const key of FILTER_KEYS) {
    const value = args?.[key];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    const name = PARAM_NAMES[key] ?? key;
    if (Array.isArray(value)) {
      // One parameter per value. An empty array contributes nothing,
      // which is what "no filter" has to look like.
      for (const item of value) {
        append(parts, name, item);
      }
    } else {
      append(parts, name, value);
    }
  }

  return parts.join('&');
};

/**
 * The cache key RTK Query uses for a set of arguments.
 *
 * `page` is left out on purpose: every page of one filter set shares a
 * cache entry, which is what lets the catalog append each page to the
 * last as you scroll. Anything else being different has to produce a
 * different key, or a newly filtered first page would be appended to
 * the results of the previous filters instead of replacing them.
 *
 * `mode` used to be left out alongside it, on the grounds that it is
 * not a filter. It is not, but it is sent - buildCatalogQuery puts it
 * on the wire, and the backend sorts on it: mode=home is the newest
 * first and returns a handful of fields, mode=list is the catalog's own
 * order. So the home strip and the catalog, which differ in nothing
 * else when no filter is applied, keyed identically and shared one
 * entry. Whichever answered first filled it for both, and because this
 * endpoint merges rather than replaces, the catalog's own first page
 * was then appended to the home strip's results instead of taking their
 * place. What that looked like: open Home, go to Movies, and the
 * catalog is the six most recently added films rather than the
 * alphabetical list it should be - and only within the minute the
 * emptied entry survives, which is what made it come and go.
 *
 * Values are sorted so that picking Action then Comedy keys the same as
 * Comedy then Action - the same query, and it should not be fetched
 * twice or accumulate into two entries.
 */
export const catalogQueryCacheKey = (args: any): string => {
  if (!args) {
    return 'none';
  }
  const stable: Record<string, unknown> = {};
  for (const key of Object.keys(args).sort()) {
    if (NOT_IN_THE_CACHE_KEY.includes(key)) {
      continue;
    }
    const value = args[key];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }
      stable[key] = [...value].map(String).sort();
    } else {
      stable[key] = value;
    }
  }
  return JSON.stringify(stable);
};

/**
 * That key, namespaced by the endpoint it belongs to.
 *
 * RTK Query keeps one flat map of cache entries, and the endpoint name
 * is the only thing separating them - its own default key is
 * `endpointName(args)` for exactly this reason. Leaving it out made the
 * three catalogs collide: an artist page renders all of them with the
 * same selectedArtistId, so they produced one identical key, shared a
 * single entry, and whichever answered first filled it for all three -
 * which is how films ended up listed under TV Shows and Autographs.
 */
export const catalogCacheKey = ({
  queryArgs,
  endpointName,
}: {
  queryArgs: any;
  endpointName: string;
}): string => `${endpointName}(${catalogQueryCacheKey(queryArgs)})`;
